import type { RailLayout } from "@/data/rail-branches";
import type { LocationRun } from "../../location/LocationRun";
import { partBranchAndPlace, partStaySegments } from "./segments";
import { stayOverlapsBranch } from "./stayOverlap";

export function branchIdForPartRun(run: LocationRun): string | null {
  const colon = run.runId.indexOf(":");
  if (colon === -1) {
    return null;
  }
  return run.runId.slice(0, colon);
}

export type PartRunPhase =
  | { kind: "upcoming"; railStartMs: number }
  | { kind: "active" }
  | { kind: "parked"; handoffMs: number };

export { partStaySegments };

export function partRunPhase(
  rail: RailLayout,
  run: LocationRun,
  playheadMs: number,
): PartRunPhase {
  const segments = partStaySegments(rail, run);
  if (segments.length === 0) {
    if (playheadMs >= run.startMs && playheadMs < run.endMs) {
      return { kind: "active" };
    }
    if (playheadMs < run.startMs) {
      return { kind: "upcoming", railStartMs: run.startMs };
    }
    return { kind: "parked", handoffMs: run.endMs };
  }
  for (const span of segments) {
    if (playheadMs >= span.startMs && playheadMs < span.endMs) {
      return { kind: "active" };
    }
  }
  const nextSegment = segments.find((span) => playheadMs < span.startMs);
  if (nextSegment !== undefined) {
    return phaseBeforeNextSegment(rail, run, playheadMs, segments, nextSegment);
  }
  let handoffMs = segments[0].endMs;
  for (const span of segments) {
    if (span.endMs <= playheadMs) {
      handoffMs = span.endMs;
    }
  }
  return { kind: "parked", handoffMs };
}

function phaseBeforeNextSegment(
  rail: RailLayout,
  run: LocationRun,
  playheadMs: number,
  segments: { startMs: number; endMs: number }[],
  nextSegment: { startMs: number; endMs: number },
): PartRunPhase {
  const first = segments[0];
  if (playheadMs < first.startMs) {
    return { kind: "upcoming", railStartMs: nextSegment.startMs };
  }
  let handoffMs = first.endMs;
  for (const span of segments) {
    if (span.endMs <= playheadMs && span.endMs < nextSegment.startMs) {
      handoffMs = span.endMs;
    }
  }
  if (playheadMs >= handoffMs && playheadMs < nextSegment.startMs) {
    const otherEnd = otherActivePartStayEndMs(rail, run, playheadMs);
    if (otherEnd !== undefined && otherEnd === nextSegment.startMs) {
      return { kind: "upcoming", railStartMs: nextSegment.startMs };
    }
    return { kind: "parked", handoffMs };
  }
  return { kind: "upcoming", railStartMs: nextSegment.startMs };
}

function otherActivePartStayEndMs(
  rail: RailLayout,
  run: LocationRun,
  playheadMs: number,
): number | undefined {
  const key = partBranchAndPlace(run, rail);
  if (key === null) {
    return undefined;
  }
  for (const stay of rail.stays) {
    if (!stayOverlapsBranch(stay, key)) {
      continue;
    }
    if (playheadMs < stay.startMs || playheadMs >= stay.endMs) {
      continue;
    }
    const placeId = stay.placeId ?? stay.rootPlaceId;
    if (placeId === key.placeId) {
      continue;
    }
    const label = stay.placeLabel;
    if (label === undefined || label === key.siteLabel) {
      continue;
    }
    return stay.endMs;
  }
  return undefined;
}

export function isPartRunActiveAt(
  rail: RailLayout,
  run: LocationRun,
  playheadMs: number,
): boolean {
  return partRunPhase(rail, run, playheadMs).kind === "active";
}
